/**
 * Books a whole outgoing bank line as a deal cost. Amount and date come from
 * the line. A closed deal's cost goes against the posted investment picked; a
 * pending or broken deal's cost is only named. Where it posts is the fund's
 * accounting framework.
 */

import { useState } from 'react';
import { Receipt } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBookBankIncomingAsDealCost } from '@/hooks/react-query/useBankIncoming';
import { useDealCostInvestments } from '@/hooks/react-query/useDealCosts';
import { useFund } from '@/hooks/react-query/useFunds';
import type { BankIncomingAdminLine } from '@/types/bank-reconciliation';
import {
  DEAL_COST_OUTCOME_LABELS,
  DEAL_COST_TYPE_LABELS,
  DealCostType,
  type DealCostInvestment,
  type DealCostOutcome,
} from '@/types/deal-costs';
import {
  CAPITALISES_CLOSED_DEAL_COSTS,
  type AccountingFramework,
} from '@/types/fund';
import {
  formatCurrency,
  formatDate,
  formatReferenceType,
} from '@/utils/formatters';

import {
  hasAnyError,
  validateDealCostFromBankLine,
  type DealCostFromBankLineErrors,
  type DealCostFromBankLineValues,
} from './dealCostFromBankLineValidators';

function outcomeEffect(
  outcome: DealCostOutcome,
  framework: AccountingFramework | undefined
): string {
  if (outcome === 'PENDING') {
    return 'is held as a deferred acquisition cost until the deal closes or breaks.';
  }
  if (outcome === 'ABORTED') return 'is expensed as a broken deal cost.';
  if (framework && CAPITALISES_CLOSED_DEAL_COSTS.has(framework)) {
    return 'is capitalised into the investment you pick.';
  }
  return 'is expensed and booked against the investment you pick.';
}

const EMPTY_VALUES: DealCostFromBankLineValues = {
  outcome: 'CLOSED',
  investment_fund_transaction_id: '',
  deal_name: '',
  cost_type: '',
  description: '',
};

function investmentLabel(investment: DealCostInvestment): string {
  const name =
    investment.description ??
    `${investment.portfolio_company_name} - ${formatReferenceType(
      investment.transaction_type.toLowerCase()
    )}`;
  return `${name} - ${formatDate(investment.transaction_date)}`;
}

export interface DealCostFromBankLineFormProps {
  line: BankIncomingAdminLine;
  onCancel: () => void;
  onBooked: () => void;
}

export function DealCostFromBankLineForm({
  line,
  onCancel,
  onBooked,
}: DealCostFromBankLineFormProps) {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [errors, setErrors] = useState<DealCostFromBankLineErrors>({});
  const { data: investments, isLoading } = useDealCostInvestments(line.fund_id);
  const book = useBookBankIncomingAsDealCost(line.fund_id);
  const { data: fund } = useFund(line.fund_id);

  const submit = async () => {
    const validationErrors = validateDealCostFromBankLine(values);
    setErrors(validationErrors);
    if (hasAnyError(validationErrors) || !values.cost_type) return;
    const shared = {
      cost_type: values.cost_type,
      description: values.description || null,
    };
    await book.mutateAsync({
      incomingId: line.id,
      data:
        values.outcome === 'CLOSED'
          ? {
              ...shared,
              outcome: 'CLOSED',
              investment_fund_transaction_id:
                values.investment_fund_transaction_id,
            }
          : {
              ...shared,
              outcome: values.outcome,
              deal_name: values.deal_name.trim(),
            },
    });
    onBooked();
  };

  const isClosed = values.outcome === 'CLOSED';

  return (
    <Card className="min-w-0">
      <CardContent className="pt-6 space-y-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Receipt className="h-5 w-5" />
            Book as deal cost
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The whole{' '}
            {formatCurrency(Math.abs(line.amount), {
              currency: line.currency ?? undefined,
            })}{' '}
            {outcomeEffect(values.outcome, fund?.accounting_framework)}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="deal-cost-outcome">Outcome</Label>
          <Select
            value={values.outcome}
            onValueChange={(value) =>
              setValues({ ...values, outcome: value as DealCostOutcome })
            }
          >
            <SelectTrigger id="deal-cost-outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(DEAL_COST_OUTCOME_LABELS) as DealCostOutcome[]).map(
                (outcome) => (
                  <SelectItem key={outcome} value={outcome}>
                    {DEAL_COST_OUTCOME_LABELS[outcome]}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {isClosed ? (
          <div className="space-y-2">
            <Label htmlFor="deal-cost-investment">Investment</Label>
            <Select
              value={values.investment_fund_transaction_id}
              onValueChange={(value) =>
                setValues({ ...values, investment_fund_transaction_id: value })
              }
            >
              <SelectTrigger id="deal-cost-investment">
                <SelectValue
                  placeholder={
                    isLoading ? 'Loading investments…' : 'Select investment'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(investments ?? []).map((investment) => (
                  <SelectItem key={investment.id} value={investment.id}>
                    {investmentLabel(investment)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && investments?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This fund has no posted investments to book a deal cost against.
              </p>
            )}
            {errors.investment_fund_transaction_id && (
              <p className="text-sm text-destructive">
                {errors.investment_fund_transaction_id}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="deal-cost-deal-name">Deal</Label>
            <Input
              id="deal-cost-deal-name"
              placeholder="e.g., Project Falcon"
              value={values.deal_name}
              onChange={(event) =>
                setValues({ ...values, deal_name: event.target.value })
              }
            />
            {errors.deal_name && (
              <p className="text-sm text-destructive">{errors.deal_name}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="deal-cost-type">Cost Type</Label>
          <Select
            value={values.cost_type}
            onValueChange={(value) =>
              setValues({ ...values, cost_type: value as DealCostType })
            }
          >
            <SelectTrigger id="deal-cost-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(DealCostType).map((type) => (
                <SelectItem key={type} value={type}>
                  {DEAL_COST_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.cost_type && (
            <p className="text-sm text-destructive">{errors.cost_type}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="deal-cost-description">Description</Label>
          <Input
            id="deal-cost-description"
            placeholder="e.g., Legal review for Series A"
            value={values.description}
            onChange={(event) =>
              setValues({ ...values, description: event.target.value })
            }
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={book.isPending}>
            {book.isPending ? 'Booking…' : 'Book deal cost'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
